import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createContext } from "use-context-selector";
import { useShellHost } from "@/shell-handle/ShellHostProvider";
import type { Site } from "./sites.graphql";

type SelectedCages = { [siteId: number]: number[] };

export interface SitesContextValue {
  sites: Site[] | undefined;
  loading: boolean;
  selectedSites: number[];
  selectedCages: SelectedCages | undefined;
  selectSite: (site: number) => void;
  clearCages: (site: number) => void;
  toggleCages: (site: number, cages: number[]) => void;
  isSiteSelected: (id: number) => boolean;
}

export const SitesContext = createContext<SitesContextValue>({
  sites: [],
  loading: false,
  selectedSites: [],
  selectedCages: undefined,
  selectSite: () => {},
  clearCages: () => {},
  toggleCages: () => {},
  isSiteSelected: () => false,
});

function decodeCages(ids: string[]): SelectedCages {
  const out: SelectedCages = {};
  for (const tuple of ids) {
    const [siteId, cageId] = tuple.split("_").map(Number);
    if (Number.isNaN(siteId) || Number.isNaN(cageId)) continue;
    out[siteId] = [...(out[siteId] ?? []), cageId];
  }
  return out;
}

function encodeCages(map: SelectedCages): string[] {
  const out: string[] = [];
  for (const siteIdStr of Object.keys(map)) {
    const siteId = Number(siteIdStr);
    for (const cageId of map[siteId] ?? []) {
      out.push(`${siteId}_${cageId}`);
    }
  }
  return out;
}

function toggleElements(arr: number[], items: number[]): number[] {
  const set = new Set(arr);
  for (const item of items) {
    if (set.has(item)) set.delete(item);
    else set.add(item);
  }
  return Array.from(set);
}

interface CloudSitesLite {
  list: { get(): Site[]; subscribe(cb: () => void): () => void };
}

export function ShellSitesContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const host = useShellHost();
  const handle = host.handle as typeof host.handle & {
    cloudSites?: CloudSitesLite;
  };

  const [sites, setSites] = useState<Site[]>(
    handle.cloudSites?.list.get() ?? [],
  );
  const [selectedSites, setSelectedSitesState] = useState<number[]>(
    handle.sites.selectedSiteIds.get(),
  );
  const [selectedCageIds, setSelectedCageIdsState] = useState<string[]>(
    handle.sites.selectedCageIds.get(),
  );

  useEffect(() => {
    if (!handle.cloudSites) return;
    return handle.cloudSites.list.subscribe(() =>
      setSites(handle.cloudSites!.list.get()),
    );
  }, [handle]);
  useEffect(
    () =>
      handle.sites.selectedSiteIds.subscribe(() =>
        setSelectedSitesState(handle.sites.selectedSiteIds.get()),
      ),
    [handle],
  );
  useEffect(
    () =>
      handle.sites.selectedCageIds.subscribe(() =>
        setSelectedCageIdsState(handle.sites.selectedCageIds.get()),
      ),
    [handle],
  );

  const selectedCages = useMemo(
    () => decodeCages(selectedCageIds),
    [selectedCageIds],
  );

  const value = useMemo<SitesContextValue>(() => {
    const selectSite = (siteId: number) => {
      const isSelected = selectedSites.includes(siteId);
      const nextSites = isSelected
        ? selectedSites.filter((id) => id !== siteId)
        : [...selectedSites, siteId];
      const nextCagesMap: SelectedCages = isSelected
        ? { ...selectedCages, [siteId]: [] }
        : selectedCages;
      handle.sites.setSelectedSiteIds(nextSites);
      handle.sites.setSelectedCageIds(encodeCages(nextCagesMap));
    };

    const toggleCages = (siteId: number, cageIds: number[]) => {
      const nextCagesMap: SelectedCages = { ...selectedCages };
      nextCagesMap[siteId] = toggleElements(
        nextCagesMap[siteId] ?? [],
        cageIds,
      );
      const nextSites = selectedSites.includes(siteId)
        ? selectedSites
        : [...selectedSites, siteId];
      handle.sites.setSelectedSiteIds(nextSites);
      handle.sites.setSelectedCageIds(encodeCages(nextCagesMap));
    };

    const clearCages = (siteId: number) => {
      const nextCagesMap: SelectedCages = { ...selectedCages };
      nextCagesMap[siteId] = [];
      handle.sites.setSelectedCageIds(encodeCages(nextCagesMap));
    };

    return {
      sites,
      loading: false,
      selectedSites,
      selectedCages,
      selectSite,
      clearCages,
      toggleCages,
      isSiteSelected: (id) => selectedSites.includes(id),
    };
  }, [sites, selectedSites, selectedCages, handle]);

  return (
    <SitesContext.Provider value={value}>{children}</SitesContext.Provider>
  );
}
